import { homePhotoProps } from "@/lib/homePhotoProps";
import { ArrowRight } from "lucide-react";
import { SHOP_URL, SAMPLE_BOARD_URL } from "@/data/siteNavigation";
import skipTrowel from "@/assets/texture-skip-trowel.webp.asset.json";
import fluted from "@/assets/texture-fluted.webp.asset.json";
import smooth from "@/assets/texture-smooth.webp.asset.json";
import knockdown from "@/assets/texture-knockdown.webp.asset.json";
import travertine from "@/assets/texture-travertine.webp.asset.json";
import limewash from "@/assets/texture-limewash.webp.asset.json";

/** Texture photos already carry their label, so no extra caption is rendered under images. */
const textures: { name: string; src?: string }[] = [
  { name: "Skip Trowel", src: skipTrowel.url },
  { name: "Fluted", src: fluted.url },
  { name: "Smooth", src: smooth.url },
  { name: "Knockdown", src: knockdown.url },
  { name: "Travertine", src: travertine.url },
  { name: "Limewash", src: limewash.url },
];

const HomeCustomSamples = () => (
  <section className="border-b border-border" aria-labelledby="custom-samples-heading">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 md:py-20 grid gap-10 lg:grid-cols-12 lg:items-center">
      <div className="lg:col-span-5">
        <p className="text-[11px] uppercase tracking-[0.28em] text-clay mb-4">Retail Custom Sample Boards</p>
        <h2 id="custom-samples-heading" className="font-heading text-3xl md:text-4xl text-foreground leading-tight">
          Custom boards to take home.
        </h2>
        <p className="mt-5 text-sm md:text-base text-muted-foreground leading-relaxed">
          Explore the color, texture, and movement of your chosen finish on a hand-applied 12 × 12-inch sample
          board. Compare it alongside your flooring, cabinetry, and furnishings, in your own lighting.
        </p>
        <p className="mt-6 border-l-2 border-clay pl-4 text-sm md:text-base text-foreground leading-relaxed">
          <strong className="font-semibold">$50 per board</strong> for preparation and application labor. Purchase a
          quart of your chosen finish product separately, or its smallest available container.
        </p>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
          Ready in 3 days after your product and color are confirmed. Your unused product is returned with the
          completed board.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5">
          <a
            href={SAMPLE_BOARD_URL}
            className="inline-flex min-h-[48px] items-center justify-center gap-2 bg-clay hover:bg-clay-dark text-primary-foreground px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors"
          >
            Order Your Project Sample
            <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
          </a>
          <a
            href={SHOP_URL}
            className="inline-flex min-h-[44px] items-center text-[11px] uppercase tracking-[0.18em] border-b border-clay text-foreground hover:text-clay transition-colors self-start sm:self-auto"
          >
            Shop Meoded Materials →
          </a>
        </div>
      </div>
      <div className="lg:col-span-7">
        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
          {textures.map((t) => (
            <li key={t.name}>
              <div className="aspect-square overflow-hidden bg-muted">
                {t.src ? (
                  <img src={t.src} {...homePhotoProps(t.src, "(min-width: 1400px) 243px, (min-width: 1024px) calc((58.333vw - 56px) / 3), (min-width: 768px) calc((100vw - 80px) / 3), (min-width: 640px) calc((100vw - 72px) / 3), calc((100vw - 44px) / 2)")} alt={`${t.name} texture preview`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-muted to-sand" aria-hidden="true" />
                )}
              </div>
              <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-foreground/80">{t.name}</p>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-muted-foreground">
          Texture previews are illustrative, not completed projects. Each retail sample is made in your selected product and color. Installation-project samples follow the separately agreed sample process.
        </p>
      </div>
    </div>
  </section>
);

export default HomeCustomSamples;
